import React from "react";
import { directionOfLocale, type PlatformLocaleTag } from "./locale-registry";

type BidiElement = "span" | "div" | "p" | "strong";

export interface BidiTextProps extends React.HTMLAttributes<HTMLElement> {
  as?: BidiElement;
  languageTag?: PlatformLocaleTag;
  direction?: "rtl" | "ltr" | "auto";
  children: React.ReactNode;
}

/** Isolates unknown or user-authored text from the surrounding shell direction. */
export const BidiText = ({ as = "span", languageTag, direction, children, style, ...rest }: BidiTextProps) => {
  const Component = as;
  const dir = direction ?? (languageTag ? directionOfLocale(languageTag) : "auto");
  return (
    <Component
      {...rest}
      lang={languageTag ?? rest.lang}
      dir={dir}
      style={{ unicodeBidi: "isolate", ...style }}
    >
      {children}
    </Component>
  );
};

/** Inline semantic isolation for names, mentions and short mixed-direction values. */
export const BidiInline = ({ children, ...rest }: Omit<BidiTextProps, "as">) => (
  <BidiText as="span" {...rest}>{children}</BidiText>
);

/**
 * Rich-message substitution without sending authored text through Trans's markup parser.
 * Use an empty ICU value for the matching tag and pass its raw display value here.
 * Parsed children are intentionally overridden; React renders string values as text.
 */
export const BidiInterpolation = ({ value, ...rest }: Omit<BidiTextProps, "children"> & {
  value: string;
  children?: React.ReactNode;
}) => <BidiText {...rest}>{value}</BidiText>;

/** Canonical identifiers remain LTR and are never localized. */
export const BidiIdentifier = ({ children, ...rest }: React.HTMLAttributes<HTMLElement>) => (
  <bdi {...rest} dir="ltr" style={{ unicodeBidi: "isolate", ...rest.style }}>{children}</bdi>
);

/** Code is LTR by default while remaining isolated inside an RTL sentence. */
export const BidiCode = ({ children, ...rest }: React.HTMLAttributes<HTMLElement>) => (
  <code {...rest} dir="ltr" style={{ unicodeBidi: "isolate", ...rest.style }}>{children}</code>
);

/** Known-language content derives direction only from registry metadata. */
export const LocalizedText = ({ languageTag, children, ...rest }: BidiTextProps & { languageTag: PlatformLocaleTag }) => (
  <BidiText {...rest} languageTag={languageTag}>{children}</BidiText>
);
